import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useSyncExternalStore } from 'react';

import { listRecruiterWorkspaces } from '@/api/recruiterWorkspaces';
import {
  getRecruiterCompanyId,
  setRecruiterCompanyId,
  subscribeRecruiterCompanyId,
} from '@/config/axios';
import { useApiQuery } from '@/hooks/api';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { queryKeys } from '@/shared/lib/queryKeys';

import type { RecruiterWorkspace } from '@/types/recruiterProject';

/** Sentinel Select value for "all workspaces" (no X-Recruiter-Company-Id). */
export const ALL_WORKSPACES_VALUE = '__all__';

export function useRecruiterWorkspace() {
  const { isRecruiter } = useCurrentUser();
  const queryClient = useQueryClient();

  const workspacesQuery = useApiQuery<RecruiterWorkspace[]>({
    queryKey: queryKeys.recruiter.workspaces(),
    queryFn: listRecruiterWorkspaces,
    enabled: isRecruiter,
  });

  const workspaces = useMemo(
    () => workspacesQuery.data ?? [],
    [workspacesQuery.data],
  );

  // localStorage writes do not re-render on their own — subscribe so the
  // header Select updates as soon as setWorkspace persists a new id.
  const storedId = useSyncExternalStore(
    subscribeRecruiterCompanyId,
    getRecruiterCompanyId,
    () => null,
  );

  const selectedWorkspaceId = useMemo(() => {
    if (!workspaces.length) return null;
    if (storedId && workspaces.some((w) => w.id === storedId)) {
      return storedId;
    }
    if (workspaces.length === 1) {
      return workspaces[0].id;
    }
    return null;
  }, [storedId, workspaces]);

  const selectedWorkspace = useMemo(
    () => workspaces.find((w) => w.id === selectedWorkspaceId) ?? null,
    [selectedWorkspaceId, workspaces],
  );

  const hasMultipleWorkspaces = workspaces.length > 1;
  const requiresWorkspaceSelection =
    hasMultipleWorkspaces && selectedWorkspaceId == null;

  const setWorkspace = useCallback(
    (companyId: string | null) => {
      setRecruiterCompanyId(companyId);
      // clear() drops the cache without notifying observers, so mounted
      // screens keep showing the previous workspace until a full reload.
      // resetQueries() notifies and refetches active queries with the new
      // X-Recruiter-Company-Id header.
      void queryClient.resetQueries();
    },
    [queryClient],
  );

  return {
    workspaces,
    selectedWorkspaceId,
    selectedWorkspace,
    hasMultipleWorkspaces,
    requiresWorkspaceSelection,
    isLoading: workspacesQuery.isLoading,
    setWorkspace,
    allWorkspacesValue: ALL_WORKSPACES_VALUE,
  };
}
